import Air from "@mui/icons-material/Air";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import InfoOutlined from "@mui/icons-material/InfoOutlined";
import Waves from "@mui/icons-material/Waves";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import IconButton from "@mui/material/IconButton";
import { alpha, Theme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { prettyPrintEnumValue } from "encamp-shared/src/utils/prettyPrintEnumValue";
import {
  HarmonizedViolationProgram,
  HarmonizedViolationResolutionStatus,
  HarmonizedViolationSeverity,
} from "generated-graphql/graphql";
import { DateTime } from "luxon";

/**
 * A row is stale when EPA stopped reporting it: its lastSeenAt predates the
 * start of the most recent successful refresh. Compared against the refresh
 * START (not finish) — a row touched mid-run has lastSeenAt >= startedAt, so
 * finishedAt would false-flag it. No refresh to compare against → never stale.
 */
export function isViolationStale(
  lastSeenAt: string,
  refreshStartedAt: string | null | undefined
): boolean {
  if (!refreshStartedAt) return false;
  return new Date(lastSeenAt).getTime() < new Date(refreshStartedAt).getTime();
}

export function formatViolationDate(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toLocaleString(DateTime.DATE_MED) : "—";
}

export function formatDiscoveryQuarter(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  if (!dt.isValid) return "—";
  const quarter = Math.floor((dt.month - 1) / 3) + 1;
  return `${dt.year} Q${quarter}`;
}

export function formatDaysToResolution(
  start: string | null | undefined,
  end: string | null | undefined
): string {
  if (!start || !end) return "—";
  const s = DateTime.fromISO(start);
  const e = DateTime.fromISO(end);
  if (!s.isValid || !e.isValid) return "—";
  return String(Math.round(e.diff(s, "days").days));
}

export function formatDiscoveryMonth(value: string | null | undefined): string {
  if (!value) return "—";
  const dt = DateTime.fromISO(value);
  return dt.isValid ? dt.toFormat("LLL yyyy") : "—";
}

const SEVERITY_COLOR: Record<HarmonizedViolationSeverity, "error" | "warning" | "info"> = {
  [HarmonizedViolationSeverity.High]: "error",
  [HarmonizedViolationSeverity.Medium]: "warning",
  [HarmonizedViolationSeverity.Low]: "info",
};

const STATUS_COLOR: Record<HarmonizedViolationResolutionStatus, "error" | "success" | "default"> = {
  [HarmonizedViolationResolutionStatus.Unresolved]: "error",
  [HarmonizedViolationResolutionStatus.Resolved]: "success",
  [HarmonizedViolationResolutionStatus.Unknown]: "default",
};

// Each program gets a leading icon (decorative — the text label stays for
// accessibility) tinted with a theme palette color, so the program reads at a
// glance without adding another fully-colored chip next to severity/status.
// All @mui/icons-material icons share one component type, so `typeof Waves`
// types the whole set (and avoids a named barrel import that the
// use-explicit-icon-import ast-grep rule forbids).
const PROGRAM_DISPLAY: Record<
  HarmonizedViolationProgram,
  { Icon: typeof Waves; iconColor: string }
> = {
  [HarmonizedViolationProgram.Water]: { Icon: Waves, iconColor: "info.main" },
  [HarmonizedViolationProgram.Air]: { Icon: Air, iconColor: "text.secondary" },
  [HarmonizedViolationProgram.Waste]: { Icon: DeleteOutline, iconColor: "warning.dark" },
};

export function ProgramChip({ program }: { program: HarmonizedViolationProgram }) {
  const { Icon, iconColor } = PROGRAM_DISPLAY[program];
  return (
    <Chip
      size="small"
      variant="outlined"
      icon={<Icon />}
      label={prettyPrintEnumValue(program)}
      // Nudge the icon right of the chip's left border so the whitespace on
      // either side of the icon reads symmetrically (default small-chip icon
      // marginLeft is a tight 4px).
      sx={{ "& .MuiChip-icon": { color: iconColor, marginLeft: "6px" } }}
    />
  );
}

// Design-system "light-filled" chip variant (see .cursor/rules/design-system.mdc
// §Chips): the role's `main` color as the label, on an 8%-alpha fill, no border —
// a gentler badge than MUI's default solid colored chip. Colors only; size/text
// stay at MUI defaults per the spec.
function lightFilledChipSx(color: "info" | "success" | "error" | "warning") {
  return {
    color: `${color}.main`,
    bgcolor: (t: Theme) => alpha(t.palette[color].main, 0.08),
  };
}

export function SeverityChip({ severity }: { severity: HarmonizedViolationSeverity }) {
  return (
    <Chip
      size="small"
      label={prettyPrintEnumValue(severity)}
      sx={lightFilledChipSx(SEVERITY_COLOR[severity])}
    />
  );
}

export function StatusChip({ status }: { status: HarmonizedViolationResolutionStatus }) {
  const color = STATUS_COLOR[status];
  // Unknown maps to the spec's neutral grey: a plain outlined chip (no color).
  if (color === "default") {
    return <Chip size="small" variant="outlined" label={prettyPrintEnumValue(status)} />;
  }
  return <Chip size="small" label={prettyPrintEnumValue(status)} sx={lightFilledChipSx(color)} />;
}

// --- Metric explanations -------------------------------------------------
//
// Severity/Program/Stale are easy to misread (e.g. "why is everything High and
// Water, and why is Medium empty?"). These explanations are surfaced two ways:
// as a rich hover tooltip on the overview headings/cards (via MetricInfoIcon)
// and as the short plain-text header hint on the matching grid column. Keep the
// wording here so both surfaces stay in sync.

type MetricExplanation = {
  /** Lead sentence — the one-line takeaway. */
  summary: string;
  /** Optional per-value breakdown rendered as a bulleted list. */
  bullets?: { term: string; desc: string }[];
  /** Optional closing caveat (rendered italic). */
  caveat?: string;
};

export const SEVERITY_EXPLANATION: MetricExplanation = {
  summary:
    "Severity comes from how EPA classifies each violation, and is set differently by program.",
  bullets: [
    {
      term: "High",
      desc: "Any air violation; a hazardous-waste (RCRA) facility EPA flags as a significant non-complier; or a water (NPDES) violation EPA flags as Reportable Non-Compliance.",
    },
    {
      term: "Medium",
      desc: "A hazardous-waste (RCRA) facility with unresolved violations that isn't a significant non-complier.",
    },
    {
      term: "Low",
      desc: "Everything else — e.g. no current unresolved RCRA violations, or a water violation with no EPA non-compliance flag.",
    },
  ],
  caveat: "Severity is EPA's classification, not a harm score.",
};

export const PROGRAM_EXPLANATION: MetricExplanation = {
  summary: "Violations grouped by EPA media program.",
  bullets: [
    { term: "Water", desc: "NPDES (Clean Water Act) discharge violations." },
    { term: "Air", desc: "Clean Air Act violations." },
    { term: "Waste", desc: "RCRA hazardous-waste violations." },
  ],
  caveat: "Waste appears only after RCRA data has been imported, so it may be empty until then.",
};

export const STALE_EXPLANATION: MetricExplanation = {
  summary: "“Stale” means our latest EPA data refresh no longer includes this violation.",
  caveat:
    "The most recent date EPA reported it is older than our last refresh, so it has dropped off EPA's published data — often because it was resolved or superseded upstream, though Encamp hasn't independently confirmed that.",
};

/** Short single-sentence hints for the grid column-header tooltips. */
export const SEVERITY_HEADER_HINT =
  "How EPA classifies the violation (not a harm score): High = any air violation, RCRA significant non-complier, or water Reportable Non-Compliance; Medium = RCRA facility with unresolved violations; Low = everything else.";
export const PROGRAM_HEADER_HINT =
  "EPA media program. Waste (RCRA) violations appear only after RCRA data has been imported.";
export const STALE_HEADER_HINT =
  "Most recent date EPA reported this violation. “Stale” means it predates our latest data refresh, so EPA no longer lists it.";

function ExplanationBody({ explanation }: { explanation: MetricExplanation }) {
  return (
    <Box sx={{ py: 0.5 }}>
      <Typography variant="caption" component="p">
        {explanation.summary}
      </Typography>
      {explanation.bullets?.length ? (
        <Box component="ul" sx={{ m: 0, mt: 0.5, pl: 2 }}>
          {explanation.bullets.map((b) => (
            <Typography key={b.term} variant="caption" component="li">
              <strong>{b.term}</strong> — {b.desc}
            </Typography>
          ))}
        </Box>
      ) : null}
      {explanation.caveat ? (
        <Typography variant="caption" component="p" sx={{ mt: 0.5, fontStyle: "italic" }}>
          {explanation.caveat}
        </Typography>
      ) : null}
    </Box>
  );
}

/**
 * A small "?" info icon that reveals a metric explanation on hover/focus.
 * `label` names the metric for screen readers (e.g. "severity").
 */
export function MetricInfoIcon({
  explanation,
  label,
}: {
  explanation: MetricExplanation;
  label: string;
}) {
  return (
    <Tooltip title={<ExplanationBody explanation={explanation} />} arrow>
      <IconButton size="small" aria-label={`About ${label}`} sx={{ p: 0.25 }}>
        <InfoOutlined fontSize="inherit" color="disabled" />
      </IconButton>
    </Tooltip>
  );
}
